import React, {useEffect, useMemo, useState} from 'react';
import {useSearchParams} from 'react-router-dom';
import Header from '../Header';
import ImageSlider from '../slider/ImageSlider';
import {
    Collapse,
    Alert,
    IconButton,
    Box
} from '@mui/material';
import BrokenImageIcon from '@mui/icons-material/BrokenImage';
import {mainRolesRoutes} from "../../global";
import { useTranslation, withTranslation } from 'react-i18next';
import './Product.css';
import dataProvider from "../../provider/api-interface";
import {Loader} from "../Loader";
import CloseIcon from "@mui/icons-material/Close";
import SelectQuantity from "../SelectQuantity";
import {useCabanaContext} from "../CabanaContext";
import QuantityAdjuster from "../QuantityAdjuster";

import {countriesCurrencies} from "../../services/CheckUtils";

function Product() {
    //cart function
    const {
        jwtToken,
        listCode,
        user,
        userType,
        cart,
        addToCart,
        wishlist,
        isLoadingWishlist,
        addProductToWishlist,
        alertType,
        alertText,
        alertOpen,
        contextSetAlertOpen
    } = useCabanaContext();

    //basically the only thing I care to know is whether the id is present or not
    const cartProductIds = useMemo(() => new Set(cart.map(p => p.id)), [cart]);

    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const productId = searchParams.get("id");
    const type = searchParams.get("type");
    const categoryID = searchParams.get("categoryId");
    const [product, setProduct] = useState<any>(null);
    const [productAdded, setProductAdded] = useState<any>(null);
    const [selectedVariation] = useState<number>(0);
    const [price] = useState<string>("");
    const [quantity, setQuantity] = useState<string>("0");
    const [productAvailableQuantity, setProductAvailableQuantity] = useState<string>("0");
    const [loadingStyle, setLoadingStyle] = useState<any>({ height: "100vh" });
    const [pageType] = useState<string | null>(type);

    // Function to retrieve product details
    useEffect(() => {
        if(!isLoadingWishlist && wishlist !== null){
            const fetchProduct = async () => {
                try {
                    const languageParam = `,"chosen_cabana_locale_id":1`;
                    const wishlistParams = userType === "gifter" ? `,"wishlist_id":"${wishlist.id}"` : "";

                    const response = await dataProvider.get(
                        `product/${productId}?filter={"cabana_locale_id" : ${1}${languageParam}${wishlistParams}}`,
                        jwtToken
                    );

                    if (response) {
                        if ("variations" in response) {
                            setLoadingStyle({ height: "100vh", opacity: "0.5" });
                        }
                        setProductAvailableQuantity(response.catalog_availability);
                        setProductAndInformation(response);
                    }
                } catch (error) {
                    console.error("Error fetching product:", error);
                }
            };

            if (productId && jwtToken) {
                fetchProduct().then(() => {});
            }
        }
    }, [isLoadingWishlist, wishlist]);

    const setProductAndInformation = (product: any) => {
        setProduct(product);
        if(wishlist.products?.length > 0){
            const foundProduct = wishlist.products.find(
                (product: any) => product.product_code === productId
            );

            if (foundProduct) {
                setProductAdded(foundProduct);
                setQuantity(String(foundProduct.wishlist_x_product_quantity_selected));
            }
        }
    }

    const currencySymbol = (currency: string): string => {
        const currencySymbol = countriesCurrencies.find((c: any) => c.currency === currency);
        return currencySymbol !== undefined ? currencySymbol.symbol : currency;
    };

    const sizeAndFit = ({ size_and_fit }: any): JSX.Element | null => {
        if (!size_and_fit || size_and_fit === 'null' || size_and_fit === 'undefined') return null;
        return (
            <div className="ProductSizeAndFit">
                <strong>{t('sizeAndFit')}</strong>
                <br />
                {size_and_fit}
                <br />
                <br />
            </div>
        );
    };

    const handleDimensions = (characteristics: any) => {
        if (!characteristics?.length) return null;

        let metadata:any;
        try {
            metadata = JSON.parse(characteristics[0]?.characteristic || '{}');
        } catch (error) {
            metadata = {};
        }

        return (
            <div className="ProductDimensionsContainer row">
                {['width', 'height', 'diameter', 'length'].map((key) => (
                    <div key={key} className="col-3 d-flex flex-column">
                        <div className="ProductDimensionsTitle">{key.charAt(0).toUpperCase() + key.slice(1)}</div>
                        {metadata[key]?.value ? (
                            <div className="ProductDimensionsValue">
                                {metadata[key].value} {metadata[key].unit ?? ''}
                            </div>
                        ) : (
                            <div className="ProductDimensionsValue">-</div> // Mostra un segnaposto se il valore è assente
                        )}
                    </div>
                ))}
            </div>
        );
    };

    const getProduct = (): JSX.Element => {
        if (isLoadingWishlist || product === null) return <div style={loadingStyle}><Loader /></div>;

        if (!product?.gallery_images || !product?.variations) return <div>Product not found</div>;

        const gallery = product.gallery_images;
        const imageSlider = gallery ? <ImageSlider imagesSlide={gallery} altTitle={product.title} /> : (
            <div style={{ height: '50vh', display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: 'rgb(179 179 179)' }}>
                <BrokenImageIcon style={{ fontSize: '100px', color: '#f0f0f0' }} />
            </div>
        );

        const selectedPrice = parseFloat(
            productAdded?.variation?.price_value || product.variations[selectedVariation]?.price_value || "0"
        );
        const formattedPrice = selectedPrice.toFixed(2).replace('.', product.variations[selectedVariation].price_currency !== 'USD' ? ',' : '.');
        const symbol = currencySymbol(product.variations[selectedVariation]?.price_currency);
        const priceString = price || `${formattedPrice} ${symbol}`;

        return (
            <div className="ProductContainer row no-gutter">
                <div className="ProductImageContainer col-12 col-lg-6">{imageSlider}</div>
                <div className="col-12 col-lg-6 ProductInfosContainer">
                    <Collapse className="FgiftCustomCollapse" in={alertOpen}>
                        <Alert
                            action={
                                <IconButton aria-label="close" color="inherit" size="small" onClick={() => contextSetAlertOpen(false)}>
                                    <CloseIcon fontSize="inherit" />
                                </IconButton>
                            }
                            severity={alertType}
                        >
                            {alertText}
                        </Alert>
                    </Collapse>
                    <div className="row ProductInfosColumn w-100">
                        <div className="col-12 col-lg-7 ProductDescriptionContainer">
                            <div className="ProductInfoContainer row">
                                <div className="ProductInfo col-6 mt-4">
                                    <div className="ProductTitle">{product.title}</div>
                                </div>
                                <div className="ProductPrice col-6 mt-4">{priceString}</div>
                                <div className="col-12 mt-2">
                                    <div className="ProductSubtitle">{product.subtitle}</div>
                                </div>
                            </div>
                            <div className="ProductDescription">
                                {product.description}
                                <br /><br />
                                {sizeAndFit(product)}
                            </div>
                            <div className="w-100">{handleDimensions(product.characteristics)}</div>
                            {selectButton()}
                            {productButton()}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const selectButton = (): JSX.Element | null => {
        if (userType === mainRolesRoutes.giftee && pageType !== 'catalogue') return null;

        if (userType === 'gifter') {
            return null
        }else{
            if(product?.catalog_availability === 0){//case you don't have the availability from the stock
                return null
            }else {
                if (productAdded === null) {
                    let cabanaMaxQuantity = (process.env.REACT_APP_CABANA_MAX_QUANTITY) ? parseInt(process.env.REACT_APP_CABANA_MAX_QUANTITY) : 20;
                    return (
                        <Box
                            sx={{
                                width: '100%',
                                marginTop: '24px'
                            }}
                        >
                            <QuantityAdjuster
                                value={parseInt(quantity)}
                                orderedValue={productAdded?.wishlist_x_product_quantity_selected || 0}
                                maxQuantityOrdered={Math.min(parseInt(productAvailableQuantity), cabanaMaxQuantity)}
                                onChange={(newQuantity: number) => setQuantity(`${newQuantity}`)}
                            />
                        </Box>
                    );
                } else {
                    return null
                }
            }
        }

        // if (productAdded?.gifter_id || productAdded?.message_alias || productAdded?.message_body) return null;
    };

    const productButton = (): JSX.Element | null => {
        if (userType === mainRolesRoutes.giftee && pageType !== 'catalogue') return null;

        if (userType === 'gifter') {
            if(product?.catalog_availability === 0){//case you don't have the availability from the stock
                return null
            }
            else{
                //we have availability from stock, but I have to check that the product is not out of stock
                if((productAdded?.wishlist_x_product_quantity_selected - productAdded?.wishlist_x_product_quantity_purchased) >= 1){
                    if(!cartProductIds.has(product?.id)){
                        return (
                            <Box
                                className={'product-detail-add-gift-container'}
                                sx={{
                                    position: 'relative'
                                }}
                            >
                                <Box
                                    className="product-detail-add-gift"
                                    onClick={() => addToCart({...product, list_code: listCode})}
                                >ADD GIFT
                                </Box>
                            </Box>
                        )
                    }
                }else{
                    return null
                }
            }
            return null
        }else{
            if(product?.catalog_availability === 0){//case you don't have the availability from the stock
                return null
            }else {
                if (productAdded === null) {
                    return (
                        <Box
                            className={'product-detail-add-gift-container'}
                            sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "24px"
                        }}>
                            <button
                                onClick={() => addProductToWishlist(product, quantity)}
                                disabled={parseInt(productAvailableQuantity) === 0}
                                    className="product-detail-add-to-wishlist">
                                {t('addItemProductPage')}
                            </button>
                        </Box>
                    );
                } else {
                    return null
                }
            }
        }

        // if (productAdded?.gifter_id || productAdded?.message_alias || productAdded?.message_body) return null;
    };

    let filters: string = "";
    let sort: string = "";
    let link: string;

    if (pageType === "catalogue") {
        const categoryParam = categoryID ? `&categoryId=${categoryID}` : "";
        link = `/${userType}/registry/catalogue?id=${wishlist?.list_code ?? ""}${filters}${sort}${categoryParam}`;
    } else {
        const basePath = `/${userType}/registry/${wishlist?.list_code ?? ""}`;
        const queryParams = [filters, sort].filter(Boolean).join(""); // Evita "?&sort=" errati
        link = queryParams ? `${basePath}?${queryParams}` : basePath;
    }

// Genera il testo di ritorno alla wishlist
    let backToText = `${t("backTo")} ${pageType === "catalogue" ? "catalogue" : wishlist?.name ?? ""}`;
    let wishlistUrl = `/${userType}/registry/${listCode}`;

    if(user === null || isLoadingWishlist || product === null){
        return <div style={{height: "100vh", width: "100%"}}><Loader/></div>;
    }

    // Render
    return (
        <div className="ProductPageContainer">
            <Header
                link={link}
                textLink={backToText}
                wishlistUrl={wishlistUrl}
                hideLogout={true}
                showWishlistLink={true}
            />
            {getProduct()}
        </div>
    );

}

export default withTranslation()(Product);
